'use client';

import {
    useState,
    useMemo,
    useRef,
    useEffect,
    useLayoutEffect,
    useTransition,
} from 'react';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';

import Base from '../../layouts/Base';
import s from './ConnectionsPage.module.scss';
import TabArea from '../../components/TabArea/TabArea';
import Bookmark from '../../components/Bookmark/Bookmark';
import { t } from '../../i18n';
import CountrySelect from '../../components/CountrySelect/CountrySelect';
import CompanySelect from '../../components/CompanySelect/CompanySelect';
import { CompanyTab } from './CompanyTab';
import { IndividualTab } from './IndividualTab';
import { NetworkTab } from './NetworkTab';

const COMPANY_SLUG = 'company';
const NETWORK_SLUG = 'network';
const INDIVIDUAL_SLUG = 'individual';

const HOLDINGS_FILTERS_STORAGE_KEY = 'holdingsFilters';
const SELECTED_COUNTRY_STORAGE_KEY = 'selectedCountry';
const SELECTED_HOLDING_PREFIX = 'selectedHolding:';

const normalizeUrlPath = (path) => {
    if (!path) return '/';
    const [pathname, search = ''] = path.split('?');
    const normalizedPathname =
        pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    return search ? `${normalizedPathname}?${search}` : normalizedPathname;
};

const buildCountryTabUrl = (countryUrl, activeTab) => {
    if (activeTab !== COMPANY_SLUG && !!activeTab) {
        return `${countryUrl}${activeTab}/`;
    }

    return countryUrl;
};

const ConnectionsPage = ({
    relativeUrl,
    countries = [],
    features,
    activeHolding,
    activeHoldingId: defaultActiveHoldingId,
    tabLinks = {
        active: 'company',
        company: '#',
        network: '#',
        individual: '#',
    },
    holdings = [],
    individuals = [],
    individualProfile,
    organizationChart = [],
    urlData,
    mediaMonitorRootUrl,
}) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [activeHoldingId, setActiveHolding] = useState(
        defaultActiveHoldingId
    );
    const [isClearingCompany, startClearCompany] = useTransition();
    const activeTab = tabLinks.active;
    const itemRefs = useRef({});
    const isFirstRun = useRef(true);

    useEffect(() => {
        if (!defaultActiveHoldingId || activeHolding !== null) return;
        const url = new URL(window.location.href);
        url.searchParams.delete('holding');
        router.replace(url.pathname + url.search);
    }, []);

    const selectedCompany = holdings.find(
        (h) => h.sourceId === activeHoldingId
    );
    const isNetworkTabDisabled = selectedCompany?.hasNetwork === false;

    const sortedHoldings = useMemo(
        () => [...holdings].sort((a, b) => a.name.localeCompare(b.name)),
        [holdings]
    );

    const updateUrlWithHolding = (baseUrl, holdingId) => {
        const params = new URLSearchParams(searchParams.toString());

        if (holdingId) params.set('holding', holdingId);
        else params.delete('holding');

        const qs = params.toString();
        return baseUrl + (qs ? `?${qs}` : '');
    };

    const tabData = useMemo(() => {
        const baseCompany = tabLinks.company.split('?')[0];
        const baseNetwork = tabLinks.network.split('?')[0];
        const baseIndividual = tabLinks.individual.split('?')[0];

        return {
            id: 'horizontal-menu',
            label: t('connectionsPage.tabMenuLabel'),
            items: [
                {
                    title: t('connectionsPage.titleCompany'),
                    url: updateUrlWithHolding(baseCompany, activeHoldingId),
                    isActive: activeTab === COMPANY_SLUG,
                },
                {
                    title: t('connectionsPage.titleNetwork'),
                    url: updateUrlWithHolding(baseNetwork, activeHoldingId),
                    isActive: activeTab === NETWORK_SLUG,
                    isDisabled: isNetworkTabDisabled,
                },
                {
                    title: t('connectionsPage.titleIndividual'),
                    url: updateUrlWithHolding(baseIndividual, activeHoldingId),
                    isActive: activeTab === INDIVIDUAL_SLUG,
                },
            ],
        };
    }, [
        tabLinks,
        activeTab,
        activeHoldingId,
        searchParams,
        isNetworkTabDisabled,
    ]);

    const activeCountry = countries.find((c) => c.isActive);

    const clearActiveHolding = () => {
        if (typeof window === 'undefined') return;
        try {
            if (activeCountry?.code) {
                sessionStorage.removeItem(
                    `${SELECTED_HOLDING_PREFIX}${activeCountry.code}`
                );
            }
        } catch {}
        const url = new URL(window.location.href);
        url.searchParams.delete('holding');
        setActiveHolding(null);
        startClearCompany(() => {
            router.replace(url.pathname + url.search);
        });
    };

    useLayoutEffect(() => {
        if (typeof window === 'undefined') return;

        try {
            const savedCountry = sessionStorage.getItem(
                SELECTED_COUNTRY_STORAGE_KEY
            );
            const currentCountry = activeCountry?.code ?? null;

            // When the user lands on a country-less connections route,
            // restore their last selected country and keep them on the
            // same active tab. Avoid replacing the current URL with an
            // equivalent URL, because that can leave browser back/forward
            // history with a blank initial render.
            // A country segment that is absent from `countries` (a company
            // whose country this dashboard does not cover) still counts as
            // a country route — leave the user on it rather than bouncing
            // them to the saved country.
            const hasCountryInUrl = !!urlData?.countryCode;

            if (savedCountry && !currentCountry && !hasCountryInUrl) {
                const targetCountry = countries.find(
                    (c) => c.code === savedCountry
                );

                if (targetCountry?.url) {
                    if (currentCountry) {
                        try {
                            sessionStorage.removeItem(
                                HOLDINGS_FILTERS_STORAGE_KEY
                            );
                        } catch {}
                    }
                    const targetUrl = buildCountryTabUrl(
                        targetCountry.url,
                        activeTab
                    );
                    const currentUrl =
                        window.location.pathname + window.location.search;

                    if (
                        normalizeUrlPath(targetUrl) !==
                        normalizeUrlPath(currentUrl)
                    ) {
                        router.replace(targetUrl);
                    }
                    return;
                }
            }

            if (currentCountry) {
                sessionStorage.setItem(
                    SELECTED_COUNTRY_STORAGE_KEY,
                    currentCountry
                );

                const key = `${SELECTED_HOLDING_PREFIX}${currentCountry}`;
                const savedHolding = sessionStorage.getItem(key);

                const url = new URL(window.location.href);
                const urlHolding = url.searchParams.get('holding');

                const hasHoldingInData =
                    !!savedHolding &&
                    holdings.some((h) => h?.sourceId === savedHolding);

                // On the very first run after a fresh page load
                // (user typed URL / bookmark), skip restoring the saved
                // holding so the clean URL is respected. Subsequent
                // effect runs (country switch, async holdings, etc.)
                // should restore normally.
                let skipRestore = false;
                if (isFirstRun.current) {
                    isFirstRun.current = false;
                    const navEntries =
                        performance.getEntriesByType('navigation');
                    if (
                        navEntries.length > 0 &&
                        navEntries[0].type === 'navigate'
                    ) {
                        skipRestore = true;
                    }
                }

                if (!urlHolding && hasHoldingInData && !skipRestore) {
                    setActiveHolding(savedHolding);
                    url.searchParams.set('holding', savedHolding);
                    window.history.replaceState({}, '', url.toString());
                }
            }
        } catch (e) {}
    }, [
        activeCountry?.code,
        activeTab,
        countries,
        router,
        holdings,
        urlData?.countryCode,
    ]);

    useLayoutEffect(() => {
        if (typeof window === 'undefined') return;
        const code = activeCountry?.code;
        if (!code) return;
        const key = `${SELECTED_HOLDING_PREFIX}${code}`;
        try {
            if (activeHoldingId) {
                sessionStorage.setItem(key, activeHoldingId);
            } else {
                sessionStorage.removeItem(key);
            }
        } catch (e) {}
    }, [activeHoldingId, activeCountry?.code]);

    const currentTabCapitalized =
        activeTab.charAt(0).toUpperCase() + activeTab.slice(1);

    const BookmarkCategories = ['Connections', activeCountry?.name].filter(
        Boolean
    );

    const bookmarkParams = new URLSearchParams(searchParams.toString());
    bookmarkParams.delete('holding');
    const bookmarkUrl = bookmarkParams.toString()
        ? `${pathname}?${bookmarkParams.toString()}`
        : pathname;

    const bookmarkTitle = individualProfile
        ? individualProfile?.name
        : currentTabCapitalized;

    return (
        <div className={s.Root}>
            <div className={s.BookmarkMobile}>
                <Bookmark
                    url={bookmarkUrl}
                    isSmall={true}
                    categories={BookmarkCategories}
                    alternativeTitle={bookmarkTitle}
                />
            </div>

            <main className={s.Main}>
                <div className={s.TabArea}>
                    <div className={s.TabAreaLeft}>
                        <div className={s.CountrySelectWrapper}>
                            <CountrySelect
                                activeTab={activeTab}
                                countries={countries}
                                defaultCountry={countries.find(
                                    (c) => c.isActive
                                )}
                            />
                        </div>
                        <div className={s.CompanySelectWrapper}>
                            <CompanySelect
                                holdings={sortedHoldings}
                                activeHoldingId={activeHoldingId}
                                setActiveHolding={setActiveHolding}
                                itemRefs={itemRefs}
                                disabled={!activeCountry}
                                activeTab={activeTab}
                                activeCountry={activeCountry}
                            />
                        </div>
                        <div className={s.TabAreaWrapper}>
                            <TabArea useNextRouter={true} {...tabData} />
                        </div>
                    </div>
                    <div className={s.TabAreaRight}>
                        <div className={s.BookmarkDesktop}>
                            <Bookmark
                                url={bookmarkUrl}
                                isSmall={false}
                                categories={BookmarkCategories}
                                alternativeTitle={bookmarkTitle}
                            />
                        </div>
                    </div>
                </div>

                <div className={s.Content}>
                    {activeTab === COMPANY_SLUG && (
                        <CompanyTab
                            features={features}
                            holdings={sortedHoldings}
                            selectedCountry={activeCountry}
                            activeHoldingId={activeHoldingId}
                            setActiveHolding={setActiveHolding}
                            defaultActiveHolding={activeHolding}
                            tabLinks={tabLinks}
                            disabled={!activeCountry}
                            relativeUrl={relativeUrl}
                            activeCountry={activeCountry}
                        />
                    )}
                    {activeTab === NETWORK_SLUG && (
                        <NetworkTab
                            urlData={urlData}
                            features={features}
                            organizationChart={organizationChart}
                            selectedCompany={selectedCompany}
                            selectedCountry={activeCountry}
                        />
                    )}
                    {activeTab === INDIVIDUAL_SLUG && (
                        <IndividualTab
                            urlData={urlData}
                            features={features}
                            selectedCompany={selectedCompany}
                            individuals={individuals.map((i) => i.properties)}
                            individualProfile={individualProfile}
                            selectedCountry={activeCountry}
                            holdings={sortedHoldings}
                            relativeUrl={relativeUrl}
                            disabled={!activeCountry}
                            mediaMonitorRootUrl={mediaMonitorRootUrl}
                            onClearCompany={clearActiveHolding}
                            isClearingCompany={isClearingCompany}
                        />
                    )}
                </div>
            </main>
        </div>
    );
};

ConnectionsPage.propTypes = {};

export const RawConnectionsPage = ConnectionsPage;
export default Base(ConnectionsPage);
